// Term Loan verification (legacy /AccountsApproval/VerifyTermLoan).
// Pending loans are verified/approved via the shared status list (Return is kept, as in legacy).
// Loans returned for update (Status '0') are corrected on the Term Loan entry screen.
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Landmark, Clock, RotateCcw } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

import {
    fetchVerifyTermLoanQueue,
    fetchVerifyTermLoanDetail,
    submitVerifyTermLoan,
    clearVerifyTermLoanDetail,
    resetVerifyTermLoan,
    selectVTLQueue, selectVTLQueueLoading, selectVTLQueueError,
    selectVTLDetail, selectVTLDetailLoading, selectVTLDetailError,
    selectVTLVerifyLoading,
} from '../../slices/accountsSlice/verifyTermLoanSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

const ACTION_VERB = {
    Verify: 'Verified Successfully',
    Approve: 'Approved Successfully',
    Return: 'Returned for Update',
    Reject: 'Rejected Successfully',
};

const FOR_CAPITAL = 'For Capital';

const Field = ({ label, value, className = '' }) => (
    <div className={className}>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words">{value || value === 0 ? value : '—'}</p>
    </div>
);

const StatusBadge = ({ status }) => (String(status) === '0' ? (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap">Return</span>
) : (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">Verification Pending</span>
));

const formatAmount = (v) => (v != null && v !== '' ? `₹ ${formatIndianCurrency(v)}` : null);

const VerifyTermLoan = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;

    const queue = useSelector(selectVTLQueue);
    const queueLoading = useSelector(selectVTLQueueLoading);
    const queueError = useSelector(selectVTLQueueError);
    const detail = useSelector(selectVTLDetail);
    const detailLoading = useSelector(selectVTLDetailLoading);
    const detailError = useSelector(selectVTLDetailError);
    const verifyLoading = useSelector(selectVTLVerifyLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const remarks = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [comment, setComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';

    const isReturned = String(selectedItem?.Status) === '0';
    // Only trust the detail that belongs to the row currently selected.
    const loan = selectedItem && detail?.Termloanid === selectedItem.Termloanid ? detail : null;

    useEffect(() => {
        if (roleId) dispatch(fetchVerifyTermLoanQueue(roleId));
    }, [roleId, dispatch]);

    useEffect(() => {
        // Legacy's status dropdown on this page keeps every option, including "Return".
        dispatch(setShowReturnButton('Yes'));
        return () => {
            dispatch(resetVerifyTermLoan());
            dispatch(resetApprovalData());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!selectedItem) return;
        dispatch(clearVerifyTermLoanDetail());
        dispatch(resetApprovalData());
        dispatch(setShowReturnButton('Yes'));
        dispatch(fetchVerifyTermLoanDetail(selectedItem.Termloanid));
        // Queue rows carry MOID; spVerifyTermloan keys Remarks on the loan number.
        if (selectedItem.MOID) {
            if (String(selectedItem.Status) !== '0' && roleId) {
                dispatch(fetchStatusList({ MOID: selectedItem.MOID, ROID: roleId, ChkAmt: 0 }));
            }
            dispatch(setSelectedMOID(selectedItem.MOID));
            dispatch(fetchRemarks({ trno: selectedItem.LoanNo, moid: selectedItem.MOID }));
        }
        setComment('');
        setShowRemarksHistory(false);
        setIsLeftPanelCollapsed(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId) dispatch(fetchVerifyTermLoanQueue(roleId));
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            dispatch(fetchVerifyTermLoanQueue(roleId));
            setSelectedItem(null);
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(false);
            dispatch(clearVerifyTermLoanDetail());
            dispatch(resetApprovalData());
        }, 800);
    };

    const handleActionClick = async (action) => {
        if (!loan) { toast.error('No term loan selected'); return; }
        if (!comment.trim()) { toast.error('Enter Note'); return; }

        const actionValue = action.type;
        const payload = {
            Termloanid: loan.Termloanid,
            Appstatus: actionValue,
            AppRemarks: comment.trim(),
            Createdby: getCurrentUser(),
            RoleId: roleId,
        };

        try {
            const result = await dispatch(submitVerifyTermLoan(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status === 'Submitted' || status === 'Submited') {
                toast.success(`Term Loan ${ACTION_VERB[actionValue] || `${actionValue}d`}`);
                finishAndRefresh();
                return;
            }
            toast.error(status || 'Error Occurred While Verification');
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While Verification');
        }
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return item.LoanNo?.toLowerCase().includes(q)
            || item.AgencyNo?.toLowerCase().includes(q)
            || item.LoanType?.toLowerCase().includes(q)
            || item.Appdate?.toLowerCase().includes(q);
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <Landmark className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">Loan {item.LoanNo}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.AgencyNo} · {item.Appdate}</p>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">{formatAmount(item.TotalAmt)}</span>
            </div>
            <div className="flex items-center justify-between gap-2 text-xs">
                <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px] text-gray-600 dark:text-gray-300">{item.LoanType}</span>
                <StatusBadge status={item.Status} />
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="text-gray-500 dark:text-gray-400 min-w-[100px]">{item.Appdate}</span>
            <span className="font-semibold text-gray-900 dark:text-white min-w-[140px]">Loan {item.LoanNo}</span>
            <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">{item.AgencyNo}</span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[110px]">{item.LoanType}</span>
            <span className="font-semibold text-gray-900 dark:text-white">{formatAmount(item.TotalAmt)}</span>
            <span className="ml-auto"><StatusBadge status={item.Status} /></span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <Landmark className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderDetails = () => (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Loan Type" value={loan.LoanType} />
                <Field label="Loan No" value={loan.LoanNo} />
                <Field label="Agency Code" value={loan.AgencyNo} />
                <Field label="Loan Applied Date" value={loan.Appdate} />
                <Field label="Disbursal Amount" value={formatAmount(loan.DisbursalAmt)} />
                <Field label="Interest Rate (%)" value={loan.IntrestRate} />
                <Field label="Processing Amount" value={formatAmount(loan.ProcessingAmt)} />
                <Field label="Total Amount" value={formatAmount(loan.TotalAmt)} />
                <Field label="Installment Start Date" value={loan.Istartdate} />
                <Field label="Installment End Date" value={loan.Ienddate} />
                <Field label="EMI Type" value={loan.Emitype} />
                <Field label="No. of Installments" value={loan.InstallmentNos} />
            </div>
            {loan.LoanType === FOR_CAPITAL && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <Field label="Bank Name" value={loan.BankName} />
                    <Field label="Bank Date" value={loan.Bkdate} />
                    <Field label="Mode of Pay" value={loan.Modeofpay} />
                    <Field label="Instrument No" value={loan.Instrumentno} />
                </div>
            )}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <Field label="Loan Purpose" value={loan.Loanpurpose} />
            </div>
        </div>
    );

    const renderActions = () => {
        if (statusLoading) {
            return (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                    </div>
                </div>
            );
        }
        if (statusError) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                </div>
            );
        }
        if (!hasActions || !enabledActions || enabledActions.length === 0) {
            return (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                    <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for this term loan</p>
                </div>
            );
        }
        return (
            <ActionButtons
                actions={enabledActions}
                onActionClick={handleActionClick}
                loading={verifyLoading}
                isVerified={true}
                comment={comment}
                showValidation={true}
            />
        );
    };

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        if (detailLoading || (!loan && !detailError)) {
            return (
                <div className="flex items-center justify-center gap-3 py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading term loan details…</span>
                </div>
            );
        }
        if (detailError || !loan) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ {detailError || 'Term loan details could not be loaded — it may no longer be pending at your level.'}</p>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Loan {loan.LoanNo}</h2>
                    <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">{formatAmount(loan.TotalAmt)}</p>
                    <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">{loan.LoanType}</span>
                        <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">Agency {loan.AgencyNo}</span>
                        <StatusBadge status={selectedItem.Status} />
                    </div>
                </div>

                {renderDetails()}

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                {isReturned ? (
                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-5 border-2 border-amber-200 dark:border-amber-700 flex items-start gap-3">
                        <RotateCcw className="w-5 h-5 text-amber-700 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                        <div>
                            <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">Returned for update</h3>
                            <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">Correct and resubmit this loan from the Term Loan entry screen — there is nothing to verify until it is resubmitted.</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <SimpleCommentInput
                            comment={comment}
                            onCommentChange={(e) => setComment(e.target.value)}
                            label="Notes"
                            placeholder="Enter verification notes…"
                            required
                            rows={3}
                            showCharCount
                        />
                        {renderActions()}
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'Term Loan Verification'} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={Landmark}
                badgeText="Term Loan Verification"
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by loan no, agency, loan type, date…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue,
                    selectedItem,
                    onItemSelect: setSelectedItem,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: 'No term loans pending verification!',
                        itemKey: 'Termloanid',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: 'Term Loan Verification',
                        icon: Landmark,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Term Loan Details',
                        icon: Landmark,
                        selectedTitle: 'Term Loan Verification',
                        emptyTitle: 'No Term Loan Selected',
                        emptyMessage: 'Select a term loan from the list to view details and take action.',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyTermLoan;
